import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const TOOLS = [
  {
    tabKey: 'text',
    image: 'https://readdy.ai/api/search-image?query=AI%20writing%20assistant%20content%20generation%20tool%20interface%20clean%20modern%20laptop%20screen%20text%20generation%20software%20dashboard%20professional&width=600&height=338&seq=ai_t001&orientation=landscape',
    category: 'ai-text-gen',
    stars: 4.8,
    reviews: 12400,
  },
  {
    tabKey: 'image',
    image: 'https://readdy.ai/api/search-image?query=AI%20image%20generator%20creative%20digital%20art%20tool%20interface%20colorful%20visual%20creation%20midjourney%20screen%20modern%20professional%20design&width=600&height=338&seq=ai_i001&orientation=landscape',
    category: 'ai-image-gen',
    stars: 4.9,
    reviews: 28500,
  },
  {
    tabKey: 'video',
    image: 'https://readdy.ai/api/search-image?query=AI%20video%20creator%20professional%20production%20tool%20interface%20timeline%20editing%20dashboard%20screen%20laptop%20modern%20software%20creative&width=600&height=338&seq=ai_v001&orientation=landscape',
    category: 'ai-video',
    stars: 4.7,
    reviews: 8900,
  },
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <i
          key={i}
          className={`text-xs ${i <= Math.floor(rating) ? 'ri-star-fill text-amber-400' : i === Math.ceil(rating) ? 'ri-star-half-fill text-amber-400' : 'ri-star-line text-gray-300'}`}
        ></i>
      ))}
    </div>
  );
}

export default function AIToolsPreview() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const toolsData = [
    {
      ...TOOLS[0],
      title: t('aiTools.textGenTitle'),
      category: t('aiTools.textGenCategory'),
      desc: 'Generate high-quality marketing copy, blog posts, and content in seconds. Perfect for marketers and business owners.',
      tags: ['Content Creation', 'Copywriting', 'SEO Writing'],
    },
    {
      ...TOOLS[1],
      title: t('aiTools.imageGenTitle'),
      category: t('aiTools.imageGenCategory'),
      desc: 'Create stunning, photorealistic images and digital art from text descriptions. Ideal for designers and marketers.',
      tags: ['Design', 'Branding', 'Marketing Visuals'],
    },
    {
      ...TOOLS[2],
      title: t('aiTools.videoCreatorTitle'),
      category: t('aiTools.videoCreatorCategory'),
      desc: 'Turn scripts and articles into professional videos with AI voiceovers and stock footage. No editing experience needed.',
      tags: ['Video Marketing', 'Content Creation', 'Automation'],
    },
  ];

  return (
    <section id="ai-tools" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-4 bg-darkBlue-50 text-darkBlue-500 px-4 py-1.5 rounded-full">
            <i className="ri-robot-line text-clairBlue-400"></i>
            {t('aiTools.label')}
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-darkBlue-500 mb-5">
            Start Making Money with AI Tools
          </h2>
          <p className="text-gray-500 text-lg leading-relaxed mb-8">Discover powerful AI tools that help you earn online through freelancing, content creation, and digital products.</p>
        </div>

        {/* Tool Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {toolsData.map((tool, i) => (
            <div key={i} className="bg-brokenWhite-100 rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all duration-300 group">
              <div className="relative h-48 overflow-hidden">
                <img
                  src={tool.image}
                  alt={tool.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-3 left-3">
                  <span className="bg-darkBlue-500/80 text-white text-xs font-semibold px-3 py-1 rounded-full backdrop-blur-sm">
                    {tool.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-display text-lg font-bold text-darkBlue-500 mb-2">{tool.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{tool.desc}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {tool.tags.map(tag => (
                    <span key={tag} className="bg-clairBlue-50 text-clairBlue-600 text-xs font-medium px-2.5 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StarRating rating={tool.stars} />
                    <span className="text-gray-400 text-xs">({tool.reviews.toLocaleString()})</span>
                  </div>
                  <button
                    onClick={() => navigate(`/ai-money`)}
                    className="flex items-center gap-1.5 text-clairBlue-500 font-semibold text-sm hover:gap-2 transition-all"
                  >
                    Learn More <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/ai-money')}
            className="inline-flex items-center justify-center gap-2 bg-clairBlue-300 hover:bg-clairBlue-200 text-darkBlue-500 font-bold text-lg px-10 py-4 rounded-xl transition-all hover:scale-105 whitespace-nowrap shadow-lg"
          >
            Explore AI Tools Now
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
